import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import { ClerkLoaded, ClerkLoading } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import Navbar from './components/web/Navbar';
import OneSignalConfig from '@/components/oneSIgnalConfig';
import ContactUs from './components/ContactUs';
import ImModal from '@/components/ImModal';
import LoadingLottie from '@/components/Loading';
import { initCustomEvent } from './utils';
import { isMobile } from '@/biz/tool';
// 导入样式
import './index.css';

// 懒加载页面组件
const Home = React.lazy(
  () =>
    import(/* webpackChunkName: "page~components~Home" */ './components/Home')
);
const ClerkCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~clerkCallbackPage" */ './pages/login/clerkCallbackPage'
    )
);
const PlatformCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~PlatformCallbackPage" */ './pages/login/PlatformCallbackPage'
    )
);
const PlatformCallbackSassPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~PlatformCallbackSassPage" */ './pages/login/PlatformCallbackSassPage'
    )
);
const ConnectError = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connectError" */ './pages/campaign/connectError'
    )
);
const ConnectSuccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connectSuccess" */ './pages/campaign/connectSuccess'
    )
);
const ErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~error" */ './pages/campaign/error'
    )
);
const MatchPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~match" */ './pages/campaign/match'
    )
);
const ConnectPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connect" */ './pages/campaign/connect'
    )
);
const Wallet = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~wallet" */ './pages/campaign/wallet'
    )
);
const WalletPaymentMethod = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~walletPaymentMethod" */ './pages/campaign/walletPaymentMethod'
    )
);
const InvitesHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~invites" */ './pages/campaign/invites'
    )
);
const DetailNew = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~detailNew" */ './pages/campaign/detailNew'
    )
);
const DealsHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~deals" */ './pages/campaign/deals'
    )
);
const ContactInfo = React.lazy(
  () => import(/* webpackChunkName: "page~contactInfo" */ './pages/contactInfo')
);
const Settings = React.lazy(
  () => import(/* webpackChunkName: "page~settings" */ './pages/settings')
);
const IconsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~settings~icons" */ './pages/settings/icons'
    )
);

const App: React.FC = () => {
  const router = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    initCustomEvent();
  }, [router]);

  return (
    <div id="app" className={isMobile() ? '' : 'webAppContainer'}>
      <ClerkLoaded>
        <Navbar />
        <OneSignalConfig />
        <Suspense
          fallback={
            <div className="flex flex-col justify-center items-center h-[calc(60vh)] w-full">
              <LoadingLottie width={160} height={160} />
              <div className="text-gray-500 text-sm">{t('common.loading')}</div>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            {/* <Route path="/web" element={<Web />} /> */}
            <Route path="/campaign/error" element={<ErrorPage />} />
            <Route path="/match" element={<MatchPage />} />
            <Route path="/connect" element={<ConnectPage />} />
            <Route path="/contactInfo" element={<ContactInfo />} />
            <Route
              path="/auth/:platform/callback"
              element={<PlatformCallbackPage />}
            />
            <Route
              path="/auth/:platform/"
              element={<PlatformCallbackSassPage />}
            />
            <Route path="/clerk" element={<ClerkCallbackPage />} />
            <Route
              path="/auth/:platform/connectError"
              element={<ConnectError />}
            />
            <Route
              path="/auth/:platform/success"
              element={<ConnectSuccess />}
            />
            {/*  */}
            {/* 以下是的新页面 */}
            {/*  */}
            <Route path="/campaign/detail/:orderId" element={<DetailNew />} />
            <Route
              path="/campaign/invites/detail/:orderId"
              element={<DetailNew />}
            />
            <Route
              path="/campaign/deals/detail/:orderId"
              element={<DetailNew />}
            />
            <Route path="/campaign/wallet/:orderId?" element={<Wallet />} />
            <Route
              path="/campaign/wallet/payment/method/update/:type"
              element={<WalletPaymentMethod />}
            />
            <Route path="/campaign/invites" element={<InvitesHome />} />
            <Route path="/campaign/deals" element={<DealsHome />} />
            {/* <Route path="/campaign/share" element={<ShareHome />} /> */}
            {/*  */}
            <Route path="/settings" element={<Settings />} />
            {process.env.REACT_APP_ENV !== 'production' && (
              <Route path="/icons" element={<IconsPage />} />
            )}
            {/*  */}
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Suspense>
        <ImModal />
      </ClerkLoaded>
      <ClerkLoading>
        <div className="flex flex-col justify-center items-center h-[calc(60vh)] w-full">
          <LoadingLottie width={160} height={160} />
          <div className="text-gray-500 text-sm">
            {t('common.loading', { defaultValue: 'Loading...' })}
          </div>
        </div>
      </ClerkLoading>
      <ContactUs />
    </div>
  );
};

export default App;
